/**
 * Onglet WhatsApp dans Agent Settings
 * Permet d'activer/configurer WhatsApp pour un agent
 */
import { useState, useEffect } from 'react'
import { getApiBase, getToken } from '../lib/utils'
import WhatsAppQRModal from './WhatsAppQRModal'
import ResponseDelayEditor from './ResponseDelayEditor'
import BusinessHoursEditor from './BusinessHoursEditor'
import WhatsAppMessagesConfig from './WhatsAppMessagesConfig'

interface WhatsAppConnection {
  id: number
  agent_id: number
  user_id: number
  status: 'disconnected' | 'connecting' | 'connected' | 'error'
  phone_number: string | null
  display_name: string | null
  qr_code: string | null
  enabled: boolean
  timezone: string
  response_delay_seconds: number
  business_hours: any
  welcome_message_enabled: boolean
  welcome_message: string | null
  closed_message_enabled: boolean
  closed_message: string | null
  human_takeover_enabled: boolean
  last_connected_at: string | null
  last_disconnected_at: string | null
  disconnect_reason: string | null
  created_at: string
  updated_at: string
}

interface WhatsAppTabProps {
  agentId: number
}

// Types pour les horaires
interface DaySchedule {
  enabled: boolean
  open: string
  close: string
}

interface BusinessHours {
  monday: DaySchedule
  tuesday: DaySchedule
  wednesday: DaySchedule
  thursday: DaySchedule
  friday: DaySchedule
  saturday: DaySchedule
  sunday: DaySchedule
}

const DEFAULT_DAY_SCHEDULE: DaySchedule = { enabled: false, open: '09:00', close: '18:00' }

// Transformer les horaires backend (start/end) vers frontend (open/close)
const transformBusinessHoursFromBackend = (backendHours: any): BusinessHours | null => {
  if (!backendHours) return null
  
  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const
  const frontendHours: BusinessHours = {
    monday: DEFAULT_DAY_SCHEDULE,
    tuesday: DEFAULT_DAY_SCHEDULE,
    wednesday: DEFAULT_DAY_SCHEDULE,
    thursday: DEFAULT_DAY_SCHEDULE,
    friday: DEFAULT_DAY_SCHEDULE,
    saturday: DEFAULT_DAY_SCHEDULE,
    sunday: DEFAULT_DAY_SCHEDULE,
  }
  
  for (const day of days) {
    const schedule = backendHours[day]
    if (schedule) {
      frontendHours[day] = {
        enabled: schedule.enabled ?? false,
        open: schedule.start ?? '09:00',
        close: schedule.end ?? '18:00'
      }
    }
  }
  return frontendHours
}

export default function WhatsAppTab({ agentId }: WhatsAppTabProps) {
  const [connection, setConnection] = useState<WhatsAppConnection | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [showQR, setShowQR] = useState(false)
  const [justConnected, setJustConnected] = useState(false)

  // Charger la connexion WhatsApp si elle existe
  useEffect(() => {
    loadConnection()
  }, [agentId])

  // Polling uniquement si status = connecting
  useEffect(() => {
    if (connection?.status !== 'connecting') {
      return
    }

    const interval = setInterval(async () => {
      try {
        const token = getToken()
        const response = await fetch(`${getApiBase()}/whatsapp/connections?agent_id=${agentId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })

        if (response.ok) {
          const data = await response.json()
          if (data.items && data.items.length > 0) {
            const newConnection = data.items[0]
            
            // Détecter l'apparition du QR code et l'afficher automatiquement
            if (newConnection.status === 'connecting' && newConnection.qr_code && !connection.qr_code) {
              setShowQR(true)
            }
            
            // Détecter la connexion réussie
            if (connection.status === 'connecting' && newConnection.status === 'connected') {
              setJustConnected(true)
              setShowQR(false) // Fermer la modal QR automatiquement
              setTimeout(() => setJustConnected(false), 5000) // Notif pendant 5s
            }
            
            setConnection(newConnection)
          }
        }
      } catch (err) {
        console.error('Polling error:', err)
      }
    }, 3000)
    
    return () => clearInterval(interval)
  }, [agentId, connection?.status])

  const loadConnection = async () => {
    try {
      setLoading(true)
      setError(null)
      const token = getToken()
      const response = await fetch(`${getApiBase()}/whatsapp/connections?agent_id=${agentId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (!response.ok) {
        throw new Error('Erreur lors du chargement')
      }

      const data = await response.json()
      // La connexion existe si items contient au moins un élément
      if (data.items && data.items.length > 0) {
        setConnection(data.items[0])
        
        // Si status = connecting, afficher le QR
        if (data.items[0].status === 'connecting' && data.items[0].qr_code) {
          setShowQR(true)
        }
      }
    } catch (err: any) {
      console.error('Error loading WhatsApp connection:', err)
      // Pas d'erreur si la connexion n'existe pas
      setConnection(null)
    } finally {
      setLoading(false)
    }
  }

  const createConnection = async () => {
    try {
      setCreating(true)
      setError(null)
      const token = getToken()
      
      const response = await fetch(`${getApiBase()}/whatsapp/connections`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          agent_id: agentId,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Paris',
          response_delay_seconds: 10
        })
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.detail || 'Erreur lors de la création')
      }

      const data = await response.json()
      setConnection(data)
      
      // Auto-ouvrir la modal QR si status = connecting et qr_code disponible
      if (data.status === 'connecting' && data.qr_code) {
        // Petite pause pour que l'UI se mette à jour
        setTimeout(() => setShowQR(true), 500)
      } else if (data.status === 'connecting') {
        // Si pas de QR code encore, recharger après 1 seconde pour le récupérer
        setTimeout(() => loadConnection(), 1000)
      }
    } catch (err: any) {
      setError(err.message)
    } finally {
      setCreating(false)
    }
  }

  const disconnectWhatsApp = async () => {
    if (!connection) return
    
    if (!confirm('Voulez-vous vraiment déconnecter WhatsApp ? Le QR code devra être scanné à nouveau.')) {
      return
    }

    try {
      const token = getToken()
      const response = await fetch(`${getApiBase()}/whatsapp/connections/${connection.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) {
        throw new Error('Erreur lors de la déconnexion')
      }

      // Recharger pour voir le bouton "Activer"
      await loadConnection()
      setShowQR(false)
    } catch (err: any) {
      setError(err.message)
    }
  }

  const updateResponseDelay = async (newDelay: number) => {
    if (!connection) return

    try {
      const token = getToken()
      const response = await fetch(`${getApiBase()}/whatsapp/connections/${connection.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          response_delay_seconds: newDelay
        })
      })

      if (!response.ok) {
        throw new Error('Erreur lors de la mise à jour')
      }

      // Recharger la connexion pour afficher la nouvelle valeur
      await loadConnection()
    } catch (err: any) {
      setError(err.message)
      throw err
    }
  }

  const updateBusinessHours = async (hours: any) => {
    if (!connection) return

    try {
      // Transformer le format frontend (open/close) vers backend (start/end)
      const transformedHours: Record<string, any> = {}
      for (const [day, schedule] of Object.entries(hours)) {
        const daySchedule = schedule as { enabled: boolean; open: string; close: string }
        transformedHours[day] = {
          enabled: daySchedule.enabled,
          start: daySchedule.open,
          end: daySchedule.close
        }
      }

      const token = getToken()
      const response = await fetch(`${getApiBase()}/whatsapp/connections/${connection.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          business_hours: transformedHours
        })
      })

      if (!response.ok) {
        throw new Error('Erreur lors de la mise à jour')
      }

      // Recharger la connexion pour afficher la nouvelle valeur
      await loadConnection()
    } catch (err: any) {
      setError(err.message)
      throw err
    }
  }

  const updateMessages = async (config: {
    welcome_message_enabled: boolean
    welcome_message: string | null
    closed_message_enabled: boolean
    closed_message: string
  }) => {
    if (!connection) return

    try {
      const token = getToken()
      const response = await fetch(`${getApiBase()}/whatsapp/connections/${connection.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(config)
      })

      if (!response.ok) {
        throw new Error('Erreur lors de la mise à jour')
      }

      // Recharger la connexion pour afficher la nouvelle valeur
      await loadConnection()
    } catch (err: any) {
      setError(err.message)
      throw err
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'connected':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
          <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
          Connecté
        </span>
      case 'connecting':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">
          <span className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></span>
          En attente du scan QR
        </span>
      case 'error':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
          <span className="w-2 h-2 bg-red-500 rounded-full"></span>
          Erreur
        </span>
      default:
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400">
          <span className="w-2 h-2 bg-gray-500 rounded-full"></span>
          Déconnecté
        </span>
    }
  }

  if (loading) {
    return (
      <div className="p-4 text-center text-sm text-gray-500">
        Chargement...
      </div>
    )
  }

  if (!connection) {
    // Pas de connexion = afficher le bouton d'activation
    return (
      <div className="space-y-4">
        <div className="rounded-lg border border-gray-200 dark:border-zinc-700 bg-gray-50 dark:bg-zinc-800/50 p-4">
          <div className="flex items-start gap-3">
            <div className="text-3xl">💬</div>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                WhatsApp Business
              </h3>
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                Connectez votre numéro WhatsApp pour permettre à cet agent de répondre automatiquement aux messages WhatsApp.
              </p>
              <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1 mb-4">
                <li>✓ Réponses automatiques 24/7</li>
                <li>✓ Horaires d'ouverture configurables</li>
                <li>✓ Historique des conversations</li>
                <li>✓ Anti-ban avec délais variables</li>
              </ul>
              <button
                onClick={createConnection}
                disabled={creating}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {creating ? 'Activation...' : '💬 Activer WhatsApp'}
              </button>
            </div>
          </div>
        </div>
        
        {error && (
          <div className="rounded-lg border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-700 dark:text-red-400">
            {error}
          </div>
        )}
      </div>
    )
  }

  // Connexion existe
  return (
    <div className="space-y-4">
      {/* Notification de succès */}
      {justConnected && (
        <div className="rounded-lg border border-green-200 dark:border-green-900 bg-green-50 dark:bg-green-900/20 p-4 flex items-start gap-3 animate-fadeIn">
          <svg className="w-6 h-6 text-green-600 dark:text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <h3 className="text-sm font-semibold text-green-900 dark:text-green-100 mb-1">
              ✅ WhatsApp connecté avec succès !
            </h3>
            <p className="text-xs text-green-700 dark:text-green-300">
              Votre compte WhatsApp est maintenant lié à cet agent. Les messages entrants seront traités automatiquement.
            </p>
          </div>
        </div>
      )}

      {/* Statut et informations */}
      <div className="rounded-lg border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            État de la connexion
          </h3>
          {getStatusBadge(connection.status)}
        </div>

        {/* Bouton QR Code si status = connecting */}
        {connection.status === 'connecting' && connection.qr_code && (
          <button
            onClick={() => setShowQR(true)}
            className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
            </svg>
            Afficher le QR Code
          </button>
        )}

        {connection.phone_number && (
          <div className="space-y-1">
            <label className="text-xs text-gray-500 dark:text-gray-400">Numéro</label>
            <div className="text-sm font-mono text-gray-900 dark:text-white">
              +{connection.phone_number}
            </div>
          </div>
        )}

        {connection.display_name && (
          <div className="space-y-1">
            <label className="text-xs text-gray-500 dark:text-gray-400">Nom d'affichage</label>
            <div className="text-sm text-gray-900 dark:text-white">
              {connection.display_name}
            </div>
          </div>
        )}

        {connection.last_connected_at && (
          <div className="space-y-1">
            <label className="text-xs text-gray-500 dark:text-gray-400">Dernière connexion</label>
            <div className="text-sm text-gray-900 dark:text-white">
              {new Date(connection.last_connected_at).toLocaleString('fr-FR')}
            </div>
          </div>
        )}

        {connection.disconnect_reason && (
          <div className="space-y-1">
            <label className="text-xs text-gray-500 dark:text-gray-400">Raison de déconnexion</label>
            <div className="text-sm text-red-600 dark:text-red-400">
              {connection.disconnect_reason}
            </div>
          </div>
        )}

        {/* Bouton Déconnecter si status = connected */}
        {connection.status === 'connected' && (
          <button
            onClick={disconnectWhatsApp}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition"
          >
            Déconnecter
          </button>
        )}
      </div>

      {/* Modal QR Code */}
      <WhatsAppQRModal
        isOpen={showQR}
        qrCode={connection.qr_code}
        onClose={() => setShowQR(false)}
      />

      {/* Éditeur délai de réponse */}
      <div className="rounded-lg border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 p-4">
        <ResponseDelayEditor
          initialDelay={connection.response_delay_seconds}
          connectionId={connection.id}
          onUpdate={updateResponseDelay}
        />
      </div>

      {/* Éditeur horaires d'ouverture */}
      <div className="rounded-lg border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 p-4">
        <BusinessHoursEditor
          initialHours={transformBusinessHoursFromBackend(connection.business_hours)}
          connectionId={connection.id}
          timezone={connection.timezone}
          onUpdate={updateBusinessHours}
        />
      </div>

      {/* Configuration messages automatiques */}
      <div className="rounded-lg border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 p-4">
        <WhatsAppMessagesConfig
          connectionId={connection.id}
          welcomeEnabled={connection.welcome_message_enabled}
          welcomeMessage={connection.welcome_message}
          closedEnabled={connection.closed_message_enabled}
          closedMessage={connection.closed_message || ''}
          onUpdate={updateMessages}
        />
      </div>

      {/* Configuration Human Takeover */}
      <div className="rounded-lg border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
              Intervention humaine
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Désactiver automatiquement l'agent quand un humain prend le relais dans une conversation
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer ml-4">
            <input
              type="checkbox"
              checked={connection.human_takeover_enabled}
              onChange={async (e) => {
                try {
                  const token = getToken()
                  const response = await fetch(`${getApiBase()}/whatsapp/connections/${connection.id}`, {
                    method: 'PATCH',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                      human_takeover_enabled: e.target.checked
                    })
                  })
                  
                  if (!response.ok) {
                    throw new Error('Erreur lors de la mise à jour')
                  }
                  
                  await loadConnection()
                } catch (err: any) {
                  setError(err.message)
                }
              }}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 dark:peer-focus:ring-emerald-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-emerald-600"></div>
          </label>
        </div>
        
        {connection.human_takeover_enabled && (
          <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-900 rounded-lg">
            <p className="text-xs text-blue-700 dark:text-blue-400">
              <strong>💡 Comment ça marche ?</strong><br/>
              Une fois qu'un humain prend le contrôle d'une conversation (via l'API ou une interface dédiée), 
              l'agent cessera automatiquement de répondre à cette conversation jusqu'à ce que le contrôle soit relâché.
            </p>
          </div>
        )}
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}
    </div>
  )
}
